// @ts-strict-ignore
import {AfterViewInit, Component, Input, OnInit, output} from "@angular/core";
import {
	animate,
	animateChild,
	animation,
	group,
	keyframes,
	query,
	state,
	style,
	transition,
	trigger,
	useAnimation,
} from "@angular/animations";
import {IAnimationOptions} from "angular-animations/common/interfaces";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {NotificationService, SimUtilityExtendedService} from "@services";
import {Translations} from "@translations";

const zoomIn = () =>
	animation(
		group([
			animate(
				"{{duration}}ms {{delay}}ms",
				keyframes([
					style({opacity: 0, easing: "ease", offset: 0}),
					style({opacity: 1, easing: "ease", offset: 0.5}),
					style({opacity: 1, easing: "ease", offset: 1}),
				]),
			),
			animate(
				"{{duration}}ms {{delay}}ms",
				keyframes([
					style({
						visibility: "visible",
						transform: "scale3d(0.3, 0.3, 0.3)",
						easing: "ease",
						offset: 0,
					}),
					style({
						transform: "scale3d(1, 1, 1)",
						easing: "ease",
						offset: 1,
					}),
				]),
			),
		]),
	);

export function useAnimationIncludingChildrenCustom(animation, options) {
	return [
		...(options && options.animateChildren === "before" ? [query("@*", animateChild(), {optional: true})] : []),
		group([
			useAnimation(animation),
			...(!options || !options.animateChildren || options.animateChildren === "together"
				? [query("@*", animateChild(), {optional: true})]
				: []),
		]),
		...(options && options.animateChildren === "after" ? [query("@*", animateChild(), {optional: true})] : []),
	];
}

const zoomInOptions: IAnimationOptions = {
	duration: 175,
	delay: 0,
};

type NullValueDisplayOptions = {
	text: string;
	substituteValue: any;
};

@Component({
	selector: "sim-admin-settings-select-to-label-transformer",
	templateUrl: "./sim-admin-settings-select-to-label-transformer.component.html",
	styleUrls: ["./sim-admin-settings-select-to-label-transformer.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175}),
		zoomOutOnLeaveAnimation({duration: 175}),
		trigger("flyInOut", [
			state("in", style({transform: "translateX(0)"})),
			transition("void => *", [style({visibility: "hidden"}), ...useAnimationIncludingChildrenCustom(zoomIn(), zoomInOptions)], {
				params: {
					delay: (zoomInOptions && zoomInOptions.delay) || 0,
					duration: zoomInOptions && zoomInOptions.duration,
				},
			}),
			transition("* => void", [animate(0, style({transform: "translateX(100%)"}))]),
		]),
	],
	standalone: false,
})
export class SimAdminSettingsSelectToLabelTransformerComponent<T> implements OnInit, AfterViewInit {
	T = Translations;

	@Input() inEditMode: boolean = false;
	@Input() labelText: string;
	@Input() selectedValue: T;
	@Input() selectableValues: T[];

	@Input() compareWithFunction: (obj1: T, obj2: T) => boolean = (_, __) => {
		return true;
	};

	@Input() getDisplayValue: (element: T) => string = (_) => {
		return "";
	};

	valueHasChanged = output<T>();

	load_completed = false;
	@Input() usingStringValues: boolean = true;
	@Input() nullValueDisplayOptions: NullValueDisplayOptions;

	constructor(
		private _simNotificationService: NotificationService,
		public _simUtilltyServiceInjection: SimUtilityExtendedService,
	) {}

	ngOnInit(): void {}

	ngAfterViewInit(): void {
		this.load_completed = true;
	}

	handleModelChange(value: T) {
		if (this.nullValueDisplayOptions && value === this.nullValueDisplayOptions.substituteValue) {
			this.selectedValue = value;
			this.valueHasChanged.emit(this.nullValueDisplayOptions.substituteValue);
		} else {
			this.selectedValue = value;
			this.valueHasChanged.emit(this.selectedValue);
		}
	}

	handleStringModelChange(value: T) {
		this.selectedValue = value;
		this.valueHasChanged.emit(value);
	}

	copyToClipboard() {
		if (typeof this.selectedValue === "string") {
			this._simUtilltyServiceInjection.copyToClipBoard(this.selectedValue);
		}
	}
}
